import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Avatar, AvatarFallback } from '@mdvip/shared/components/ui/avatar';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { initials } from '@mdvip/shared/lib/initials';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useActiveMember } from '@/hooks/members/useActiveMember';
import { memberAge } from '@/lib/members/memberAge';
import { useActiveMemberStore } from '@/stores/activeMemberStore';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** The active member's name heading — a stable id so CNP-1174's landing modal can move focus here
 * after a pick, without either component importing the other. */
export const MEMBER_CONTEXT_BAR_NAME_ID = 'member-context-bar-name';

export interface MemberContextBarProps {
  /** CNP-825's patient switcher. Rendered only in the ready state — nothing to switch away from
   * while there's no member, and a stale control serves no one mid-error. */
  switcher?: React.ReactNode;
  /** AWP-story actions (Manage AWP, etc.), ready state only — same reasoning as `switcher`. */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Who the provider is working on: avatar, name, and an "Age N · MDVIP ID ..." meta line, reading
 * `useActiveMember()` directly rather than taking the member as a prop — every AWP surface needs
 * the same active member, and a prop would just be threading this same hook's result down.
 *
 * `NOT_FOUND` (the contact id no longer resolves — removed from the practice, a stale id) is not
 * rendered as an error: it clears the active-member selection instead, which is what lets CNP-1174's
 * landing modal reopen with a real pick rather than leaving the provider stuck looking at a message
 * about a patient who is gone.
 */
export function MemberContextBar({ switcher, actions, className }: MemberContextBarProps) {
  const { t } = useTranslation('providers');
  const { data, isPending, isError, error, refetch, activeMemberId } = useActiveMember();

  const code = isError ? extractGqlErrorCode(error) : null;
  const notFound = code === 'NOT_FOUND';

  useEffect(() => {
    if (notFound) useActiveMemberStore.getState().reset();
  }, [notFound]);

  if (!activeMemberId || notFound) {
    // The switcher is the one thing worth showing with nobody selected yet — everything else here
    // (name, meta, actions) has nothing to describe.
    return switcher ? (
      <div className={cn('@container/mcb', className)} data-slot="member-context-bar" data-state="empty">
        {switcher}
      </div>
    ) : null;
  }

  if (isError) {
    return (
      <div className={cn('@container/mcb', className)} data-slot="member-context-bar" data-state="error">
        <Alert variant="destructive">
          <Icon name="alertTriangle" weight="solid" />
          <AlertTitle>{t('members.contextBar.loadErrorTitle')}</AlertTitle>
          <AlertDescription>
            <p>{t('members.contextBar.loadErrorBody')}</p>
            <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => refetch()}>
              <Icon name="refreshCw" size="xs" />
              {t('members.contextBar.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isPending || !data) {
    return (
      <div
        className={cn('@container/mcb', className)}
        data-slot="member-context-bar"
        data-state="loading"
        aria-busy="true"
      >
        <span className="sr-only" role="status">
          {t('members.contextBar.loadingLabel')}
        </span>
        <div className="flex flex-col gap-4 @3xl/mcb:flex-row @3xl/mcb:items-center @3xl/mcb:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-4">
            <Skeleton className="size-11 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-col gap-2">
              <Skeleton className="h-5 w-56" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
          <Skeleton className="h-11 w-full rounded-lg @3xl/mcb:w-80" />
        </div>
      </div>
    );
  }

  const age = memberAge(data.dateOfBirth);
  const metaParts = [
    age !== null ? t('members.contextBar.age', { age }) : null,
    data.mdvipId ? t('members.contextBar.mdvipId', { id: data.mdvipId }) : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className={cn('@container/mcb', className)} data-slot="member-context-bar" data-state="ready">
      <div className="flex flex-col gap-4 @3xl/mcb:flex-row @3xl/mcb:items-center @3xl/mcb:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-4">
          <Avatar className="size-11 shrink-0" aria-hidden="true">
            <AvatarFallback className="bg-gradient-to-br from-avatar-gradient-from to-avatar-gradient-to text-sm font-bold text-foreground">
              {initials({ firstName: data.firstName, lastName: data.lastName })}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-baseline gap-2">
              <h2
                id={MEMBER_CONTEXT_BAR_NAME_ID}
                tabIndex={-1}
                className={cn('text-h3 font-semibold tracking-tight text-foreground rounded-sm', FOCUS)}
              >
                {[data.firstName, data.lastName].filter(Boolean).join(' ')}
              </h2>
            </div>
            {metaParts.length > 0 && <p className="mt-2 text-xs text-muted-foreground">{metaParts.join(' · ')}</p>}
          </div>
          {actions}
        </div>
        {switcher}
      </div>
    </div>
  );
}
